import { Link, Pause, Play, Song } from '@tunasong/icons'
import { dayjs } from '@tunasong/models'
import type { Entity, Media, Persisted } from '@tunasong/schemas'
import {EntityIcon, PulseProgress, useNavigateToEntity, IconButton, ListItemIcon, ListItemText, ListItemSecondaryAction, ListItemButton, type ListItemButtonProps, Typography} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, MouseEvent } from 'react'
import { useCallback } from 'react'
import { useLinkedSong } from './linked-song.js'
import styles from './playlist-list-item.module.css'

export interface PlayListItemProps extends ListItemButtonProps {
  ref?: React.Ref<HTMLDivElement>
  entity: Persisted<Media>
  /** When set, an unlinked entry shows a link button that fires this. */
  onLinkSong?: (media: Persisted<Media>) => void
  onPlayClicked?: (ev: MouseEvent) => void
  playing?: boolean
  progressLabel?: string
  showUpdated?: boolean
  showPageButton?: boolean
  showPlayButton?: boolean
}

export const PlayListItem: FC<PlayListItemProps> = props => {
  const {
    entity,
    progressLabel,
    playing = false,
    onLinkSong,
    onPlayClicked,
    showPlayButton = false,
    showPageButton = true,
    showUpdated = false,
    className,
    ...restProps
  } = props

  // Shared definition with the performance stage — the melody icon shows only
  // when a real song/page document is linked (a bare folder parent does not count).
  const { song } = useLinkedSong(entity)

  const navigateToEntity = useNavigateToEntity()
  const handleNavigate = useCallback(
    (entityOrEntityId?: Persisted<Entity> | string) => (ev: MouseEvent) => {
      const entityId = typeof entityOrEntityId === 'string' ? entityOrEntityId : entityOrEntityId?.id
      if (entityId) {
        ev.preventDefault()
        ev.stopPropagation()
        const navigationMode = ev.shiftKey ? 'dialog' : 'page'
        navigateToEntity({ id: entityId }, { navigationMode })
      }
    },
    [navigateToEntity]
  )

  const handleLinkSong = useCallback(
    (ev: MouseEvent) => {
      ev.preventDefault()
      ev.stopPropagation()
      onLinkSong?.(entity)
    },
    [entity, onLinkSong]
  )

  const secondary = showUpdated ? `Updated ${dayjs(entity.updatedAt).fromNow()}` : undefined

  return (
    <ListItemButton
      className={cn(styles.listItem, className)}
      {...restProps}
    >
      <ListItemIcon>
        <EntityIcon entity={entity} size={'medium'} />
      </ListItemIcon>
      <ListItemText primary={entity.name} secondary={secondary} />
      <ListItemSecondaryAction>
        {showPlayButton && (
          <IconButton onClick={onPlayClicked} title="Toggle play">
            {playing ? (
              <>
                <Typography variant="caption">{progressLabel}</Typography>
                <PulseProgress size="small" />
                <Pause />
              </>
            ) : (
              <Play />
            )}
          </IconButton>
        )}

        {showPageButton && song ? (
          <IconButton onClick={handleNavigate(song.id)} title="Go to song/page">
            <Song />
          </IconButton>
        ) : null}
        {showPageButton && !song && onLinkSong ? (
          <IconButton onClick={handleLinkSong} title="Link a song or page">
            <Link />
          </IconButton>
        ) : null}
      </ListItemSecondaryAction>
    </ListItemButton>
  )
}
